import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import SalaryBreakdown from '../components/SalaryBreakdown';
import { inr, monthLabel, myApi, MySalaryMonth, PayrollLineDetail } from '../payroll.api';

/**
 * The signed-in employee's paid salaries, each opening to its full breakdown. On screen only —
 * salary slips as PDF are issued by the accounts department.
 */
export default function MySalaryPage() {
  const [months, setMonths] = useState<MySalaryMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MySalaryMonth | null>(null);
  const [detail, setDetail] = useState<PayrollLineDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    myApi
      .salaries()
      .then((m) => {
        setMonths(m);
        if (m.length) setSelected(m[0]);
      })
      .catch((e) => setError(logError({ message: 'Could not load your salary', error: e, code: 'MY_SALARY_FAILED' })))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setDetailLoading(true);
    setDetail(null);
    myApi
      .salary(selected.year, selected.month)
      .then(setDetail)
      .catch((e) => setError(logError({ message: 'Could not load that month', error: e, code: 'MY_SALARY_MONTH_FAILED' })))
      .finally(() => setDetailLoading(false));
  }, [selected]);

  return (
    <Stack gap={3}>
      <SectionTitle title='MY SALARY' />
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={3} alignItems='flex-start'>
        <Card title='PAID MONTHS' sx={{ minWidth: 340, width: { xs: '100%', lg: 'auto' } }}>
          {!loading && months.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No salary has been paid through payroll yet.
            </Typography>
          ) : (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align='right'>Gross</TableCell>
                  <TableCell align='right'>Deductions</TableCell>
                  <TableCell align='right'>Net</TableCell>
                  <TableCell>Paid on</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map((m) => {
                  const active = selected?.year === m.year && selected?.month === m.month;
                  return (
                    <TableRow
                      key={`${m.year}-${m.month}`}
                      hover
                      selected={active}
                      onClick={() => setSelected(m)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{monthLabel(m.year, m.month)}</TableCell>
                      <TableCell align='right'>{inr(m.earnedGross)}</TableCell>
                      <TableCell align='right'>{inr(m.totalDeductions)}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700 }}>
                        {inr(m.netPay)}
                      </TableCell>
                      <TableCell>
                        {m.paymentDate ? new Date(`${m.paymentDate}T00:00:00`).toLocaleDateString('en-IN') : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card title='BREAKDOWN' sx={{ flex: 1, width: '100%' }}>
          {detailLoading && <LinearProgress />}
          {detail ? (
            <SalaryBreakdown line={detail} />
          ) : (
            !detailLoading && (
              <Typography variant='body2' color='text.secondary'>
                Choose a month to see how the salary was worked out.
              </Typography>
            )
          )}
          <Typography variant='caption' color='text.secondary'>
            For a salary slip (PDF), please ask the accounts department.
          </Typography>
        </Card>
      </Stack>
    </Stack>
  );
}
